/* Mild theme · theme.js
   Custom elements: drawers, cart drawer (ajax), product form, variant picker,
   quantity input, media gallery, sticky add-to-cart bar, related products.
*/
(() => {
  const routes = window.theme.routes;
  const strings = window.theme.strings;

  /* ---------- helpers ---------- */
  const formatMoney = (cents) => {
    const format = window.theme.moneyFormat || '{{amount}} zł';
    const value = (cents / 100).toFixed(2);
    const [int, dec] = value.split('.');
    const withSpaces = int.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    let out;
    if (format.includes('amount_no_decimals')) out = withSpaces;
    else if (format.includes('amount_with_comma_separator')) out = `${withSpaces},${dec}`;
    else out = `${withSpaces},${dec}`;
    return format.replace(/\{\{\s*amount[^}]*\}\}/, out);
  };

  const toast = (() => {
    let el;
    let timer;
    return (msg) => {
      if (!el) { el = document.createElement('div'); el.className = 'toast'; el.setAttribute('role', 'status'); document.body.appendChild(el); }
      el.textContent = msg;
      el.classList.add('is-visible');
      clearTimeout(timer);
      timer = setTimeout(() => el.classList.remove('is-visible'), 2200);
    };
  })();

  const fetchSections = async (ids, url = window.location.pathname) => {
    const res = await fetch(`${url}${url.includes('?') ? '&' : '?'}sections=${ids.join(',')}`);
    return res.json();
  };

  const htmlFrom = (html, selector) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return selector ? doc.querySelector(selector) : doc;
  };

  /* ---------- drawers ---------- */
  const drawers = {
    open(id) {
      const d = document.getElementById(id);
      if (!d) return;
      d.returnFocus = document.activeElement;
      d.classList.add('is-open');
      d.setAttribute('aria-hidden', 'false');
      document.body.classList.add('drawer-open');
      const focusable = d.querySelector('button[data-drawer-close], a[href], button:not(:disabled), input:not([type="hidden"])');
      focusable && focusable.focus({ preventScroll: true });
      document.querySelectorAll(`[aria-controls="${id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
    },
    close(d) {
      if (!d) return;
      d.classList.remove('is-open');
      d.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('drawer-open');
      if (d.returnFocus && d.returnFocus.isConnected) d.returnFocus.focus({ preventScroll: true });
      document.querySelectorAll(`[aria-controls="${d.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    },
    closeAll() { document.querySelectorAll('[data-drawer].is-open').forEach((d) => drawers.close(d)); },
  };

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) { e.preventDefault(); drawers.open(opener.dataset.drawerOpen); return; }
    const cartOpener = e.target.closest('[data-cart-open]');
    if (cartOpener && document.getElementById('CartDrawer') && !document.querySelector('[data-cart-page]')) {
      e.preventDefault(); drawers.open('CartDrawer'); return;
    }
    const closer = e.target.closest('[data-drawer-close]');
    if (closer) { drawers.close(closer.closest('[data-drawer]')); }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') drawers.closeAll();
    if (e.key !== 'Tab') return;
    const drawer = document.querySelector('[data-drawer].is-open');
    if (!drawer) return;
    const focusable = [...drawer.querySelectorAll('a[href], button:not(:disabled), input:not([type="hidden"]), select, textarea, [tabindex="0"]')].filter(el => el.getClientRects().length);
    const first = focusable[0], last = focusable[focusable.length - 1];
    if (e.shiftKey && (document.activeElement === first || !drawer.contains(document.activeElement))) { e.preventDefault(); last?.focus(); }
    else if (!e.shiftKey && (document.activeElement === last || !drawer.contains(document.activeElement))) { e.preventDefault(); first?.focus(); }
  });
  document.addEventListener('click', (e) => {
    if (e.target.closest('#MenuDrawer a[href]')) drawers.close(document.getElementById('MenuDrawer'));
  });

  /* ---------- cart ---------- */
  const cart = {
    async refresh(openDrawer = false) {
      const drawer = document.getElementById('CartDrawer');
      const [data, state] = await Promise.all([
        drawer ? fetchSections(['cart-drawer'], routes.root) : Promise.resolve({}),
        fetch(`${routes.cart}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json()),
      ]);
      if (drawer && data['cart-drawer']) {
        const fresh = htmlFrom(data['cart-drawer'], '#CartDrawer');
        if (fresh) {
          const focused = document.activeElement;
          const focusedKey = focused?.closest('.cart-line')?.dataset.key;
          const wasInside = drawer.contains(focused);
          const kind = focused?.hasAttribute('data-qty') ? `[data-qty="${focused.dataset.qty}"]` : focused?.hasAttribute('data-line-qty') ? '[data-line-qty]' : '[data-drawer-close]';
          drawer.querySelector('.drawer__panel').replaceWith(fresh.querySelector('.drawer__panel'));
          if (wasInside) {
            const row = [...drawer.querySelectorAll('.cart-line')].find(el => el.dataset.key === focusedKey);
            (row?.querySelector(kind) || drawer.querySelector('[data-drawer-close]'))?.focus({ preventScroll: true });
          }
        }
      }
      const count = document.querySelector('[data-cart-count]');
      if (count && state) { count.textContent = state.item_count; count.hidden = state.item_count === 0; }
      const page = document.querySelector('[data-cart-page]');
      if (page) {
        const html = await fetch(`${routes.cart}?section_id=${encodeURIComponent(page.dataset.sectionId)}`).then((r) => r.text());
        const fresh = htmlFrom(html, '[data-cart-page]');
        if (fresh) page.replaceWith(fresh);
      }
      if (openDrawer && drawer) drawers.open('CartDrawer');
      document.dispatchEvent(new CustomEvent('cart:updated'));
    },
    async add(formData) {
      const isJson = !(formData instanceof FormData);
      const res = await fetch(`${routes.cartAdd}.js`, { method: 'POST', headers: isJson ? { 'Content-Type': 'application/json', Accept: 'application/json' } : { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' }, body: isJson ? JSON.stringify(formData) : formData });
      const json = await res.json();
      if (!res.ok) throw new Error(json.description || json.message || strings.cartError);
      return json;
    },
    async change(id, quantity) {
      const res = await fetch(`${routes.cartChange}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id, quantity }) });
      if (!res.ok) throw new Error(strings.cartError);
      return res.json();
    },
  };

  // Serialize cart mutations using stable line keys; changing one row never cancels another.
  let cartQueue = Promise.resolve();
  const queueChange = (input, quantity) => {
    const line = input.closest('.cart-line');
    const key = line.dataset.key;
    if (!Number.isInteger(quantity) || quantity < 0) { toast(window.theme?.strings?.qtyInvalid || 'Wpisz poprawną ilość.'); return; }
    line.classList.add('is-loading');
    cartQueue = cartQueue.catch(() => {}).then(async () => {
      try { await cart.change(key, quantity); await cart.refresh(); }
      catch (err) { toast(err.message); line.classList.remove('is-loading'); }
    });
  };
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-line-qty]');
    if (input) queueChange(input, Number(input.value));
  });
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-line-remove]');
    if (btn) queueChange(btn, 0);
  });

  /* ---------- quantity input ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-qty]');
        if (!btn) return;
        const min = Number(this.input.min || 0);
        const next = Math.max(min, Number(this.input.value || 0) + Number(btn.dataset.qty));
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------- product form (ajax add) ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.button = this.form.querySelector('[data-add-to-cart]');
      this.error = this.form.querySelector('[data-form-error]');
      this.form.addEventListener('submit', (e) => this.submit(e));
    }
    async submit(e) {
      e.preventDefault();
      if (!this.button || this.button.disabled || this.busy) return;
      if (!this.form.reportValidity()) return;
      this.busy = true;
      const label = this.button.querySelector('[data-add-to-cart-text]');
      const original = label ? label.textContent : '';
      this.button.setAttribute('aria-disabled', 'true');
      this.button.setAttribute('aria-busy', 'true');
      if (label) label.textContent = strings.adding;
      if (this.error) this.error.hidden = true;
      try {
        const fd = new FormData(this.form);
        fd.set('sections', 'cart-drawer');
        const addons = [...new Set([...this.form.querySelectorAll('[data-addon]:checked'), ...document.querySelectorAll(`[data-addon][form="${this.form.getAttribute('id')}"]:checked`)])].filter((a) => !a.disabled);
        const addon = addons[0];
        fd.delete('addon');
        if (addons.length) {
          const qty = Number(fd.get('quantity') || 1);
          await cart.add({ items: [{ id: Number(fd.get('id')), quantity: qty }, ...addons.map((a) => ({ id: Number(a.value), quantity: 1 }))] });
          addons.forEach((a) => { a.checked = false; a.disabled = true; const box = a.closest('.addon'); const sub = box?.querySelector('.addon__sub'); if (sub) sub.textContent = box.dataset.inCartLabel || 'Już w koszyku'; });
        } else {
          await cart.add(fd);
        }
        if (label) label.textContent = strings.added;
        const page = this.closest('product-page');
        const xsellVariant = page?.dataset.xsellVariant;
        const xsell = page?.querySelector('[data-xsell]');
        if (xsell && xsellVariant && !addon && !sessionStorage.getItem(`xsell-${xsellVariant}`)) {
          try { await cart.refresh(false); } catch (_) {}
          crossSell.open(xsell, xsellVariant);
        } else {
          try { await cart.refresh(true); }
          catch (_) { window.location.assign(routes.cart); return; }
        }
        if (!document.getElementById('CartDrawer')) window.location.assign(routes.cart);
        setTimeout(() => { if (label && !this.button.disabled && !this.busy) label.textContent = original; }, 1200);
      } catch (err) {
        if (label) label.textContent = original;
        if (this.error) { this.error.textContent = err.message; this.error.hidden = false; } else toast(err.message);
      } finally {
        this.button.removeAttribute('aria-disabled');
        this.button.removeAttribute('aria-busy');
        this.busy = false;
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------- variant picker ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('[data-variants]');
      this.variants = data ? JSON.parse(data.textContent) : [];
      this.sectionId = this.dataset.section;
      this.url = this.dataset.url;
      this.addEventListener('change', () => this.onChange());
    }
    selectedOptions() {
      return [...this.querySelectorAll('fieldset')].map((fs) => {
        const checked = fs.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }
    currentVariant() {
      const opts = this.selectedOptions();
      return this.variants.find((v) => v.options.every((o, i) => o === opts[i]));
    }
    async onChange() {
      const revision = this.revision = (this.revision || 0) + 1;
      const variant = this.currentVariant();
      const page = this.closest('product-page');
      const form = document.getElementById(`ProductForm-${this.sectionId}`);
      const idInput = form && form.querySelector('[data-variant-id]');
      const button = form && form.querySelector('[data-add-to-cart]');
      const label = button && button.querySelector('[data-add-to-cart-text]');
      const stickyAdd = page && page.querySelector('[data-sticky-add]');
      const stickyPrice = page && page.querySelector('[data-sticky-price]');

      if (!variant) {
        if (button) { button.disabled = true; if (label) label.textContent = strings.unavailable; }
        if (idInput) idInput.value = '';
        if (stickyAdd) { stickyAdd.disabled = true; stickyAdd.textContent = strings.unavailable; }
        return;
      }
      if (idInput) idInput.value = variant.id;
      const weight = page?.querySelector('[data-product-weight]');
      if (weight) weight.textContent = `(${variant.options[Number(weight.dataset.optionIndex)]} g)`;
      if (button) {
        button.disabled = !variant.available;
        if (label) label.textContent = variant.available ? strings.addToCart : strings.soldOut;
      }
      if (stickyAdd) { stickyAdd.disabled = !variant.available; stickyAdd.textContent = variant.available ? strings.addToCart : strings.soldOut; }
      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
      const stickyVariant = page?.querySelector('[data-sticky-variant]');
      if (stickyVariant) stickyVariant.textContent = variant.title === 'Default Title' ? '' : variant.title + (page.querySelector('[data-product-weight]') ? ' g' : '');
      page?.querySelector('sticky-bar')?.refresh?.();
      const priceNow = document.querySelector(`#Price-${this.sectionId} .price__current`);
      if (priceNow) priceNow.textContent = formatMoney(variant.price);
      const compareNow = document.querySelector(`#Price-${this.sectionId} .price__compare`);
      if (compareNow) compareNow.hidden = true;
      const portion = page?.querySelector('[data-portion-price]');
      if (portion) {
        const wEl = page.querySelector('[data-product-weight]');
        const grams = wEl ? parseFloat(variant.options[Number(wEl.dataset.optionIndex)]) : 0;
        if (grams > 0) { portion.hidden = false; portion.textContent = `${formatMoney(Math.round(variant.price * 100 / grams))} / 100 g`; }
        else portion.hidden = true;
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);

      if (variant.featured_media && page) {
        const gallery = page.querySelector('media-gallery');
        gallery && gallery.show(variant.featured_media.id);
      }

      try {
        const html = await fetch(`${this.url}?variant=${variant.id}&section_id=${this.sectionId}`).then((r) => r.text());
        if (revision !== this.revision) return;
        const doc = htmlFrom(html);
        const freshPrice = doc.querySelector(`#Price-${this.sectionId}`);
        const price = document.getElementById(`Price-${this.sectionId}`);
        if (freshPrice && price) price.innerHTML = freshPrice.innerHTML;
        const freshNotice = doc.querySelector('[data-sold-out-notice]');
        const notice = page && page.querySelector('[data-sold-out-notice]');
        if (notice && !freshNotice) notice.remove();
        if (!notice && freshNotice && page) page.querySelector('.product__buy').appendChild(freshNotice);
      } catch (_) { /* price stays as is */ }
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ---------- media gallery ---------- */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        const thumb = e.target.closest('[data-gallery-thumb]');
        if (thumb) this.show(thumb.dataset.galleryThumb);
        const step = e.target.closest('[data-gallery-step]');
        if (step) this.step(Number(step.dataset.galleryStep));
      });
      let startX = 0;
      const main = this.querySelector('[data-gallery-main]');
      if (!main) return;
      main.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
      main.addEventListener('touchend', (e) => {
        const dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 40) this.step(dx < 0 ? 1 : -1);
      });
    }
    slides() { return [...this.querySelectorAll('.gallery__slide')]; }
    show(id) {
      this.slides().forEach((s) => s.classList.toggle('is-active', String(s.dataset.mediaId) === String(id)));
      this.querySelectorAll('[data-gallery-thumb]').forEach((t) => {
        const active = String(t.dataset.galleryThumb) === String(id);
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      this.querySelectorAll('video').forEach((v) => v.pause());
      const counter = this.querySelector('[data-gallery-count]');
      if (counter) counter.textContent = `${this.slides().findIndex(s => s.classList.contains('is-active')) + 1} / ${this.slides().length}`;
    }
    step(dir) {
      const slides = this.slides();
      const i = slides.findIndex((s) => s.classList.contains('is-active'));
      const next = slides[(i + dir + slides.length) % slides.length];
      next && this.show(next.dataset.mediaId);
    }
  }
  customElements.define('media-gallery', MediaGallery);

  /* ---------- sticky bar ---------- */
  class StickyBar extends HTMLElement {
    connectedCallback() {
      const page = this.closest('product-page');
      const form = page?.querySelector('product-form form');
      const trigger = page?.querySelector('[data-add-to-cart]');
      if (!form || !trigger) return;
      this.hidden = false;
      const button = this.querySelector('[data-sticky-add]');
      this.refresh = () => {
        const rect = trigger.getBoundingClientRect();
        const below = rect.top >= window.innerHeight - 70;
        const above = rect.bottom < 90;
        const compact = window.matchMedia('(max-width: 749px)').matches;
        const show = above || (compact && below);
        this.chooseFirst = below;
        this.classList.toggle('is-visible', show);
        this.inert = !show;
        document.body.classList.toggle('has-sticky-bar', show);
        button.disabled = below ? false : trigger.disabled;
        button.textContent = below ? (this.dataset.chooseLabel || 'Wybierz opcję') : (trigger.disabled ? strings.soldOut : strings.addToCart);
      };
      this.io = new IntersectionObserver(this.refresh, {threshold:[0,1],rootMargin:'-90px 0px -70px 0px'});
      this.io.observe(trigger);
      this.mo = new MutationObserver(this.refresh);
      this.mo.observe(trigger, {attributes:true,attributeFilter:['disabled']});
      window.addEventListener('resize', this.refresh, {passive:true});
      button.addEventListener('click', () => {
        if (this.chooseFirst) {
          const destination = page.querySelector('variant-picker') || form;
          destination.scrollIntoView({behavior:window.matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth',block:'center'});
          const option = destination.querySelector('input:checked,button:not([disabled])');
          option?.focus({preventScroll:true});
        } else form.requestSubmit();
      });
      this.refresh();
    }
    disconnectedCallback() {
      this.io?.disconnect();this.mo?.disconnect();window.removeEventListener('resize',this.refresh);
      document.body.classList.remove('has-sticky-bar');
    }
  }
  customElements.define('sticky-bar', StickyBar);

  /* ---------- related products (recommendations API) ---------- */
  const related = document.querySelector('[data-related-products]');
  if (related && related.dataset.url) {
    const io = new IntersectionObserver(async ([entry]) => {
      if (!entry.isIntersecting) return;
      io.disconnect();
      try {
        const html = await fetch(related.dataset.url).then((r) => r.text());
        const fresh = htmlFrom(html, '[data-related-products]');
        if (fresh && fresh.innerHTML.trim()) related.innerHTML = fresh.innerHTML;
      } catch (_) { /* leave empty */ }
    }, { rootMargin: '400px' });
    io.observe(related);
  }

  // Carry the selected product into the existing contact form; never submits automatically.
  const contactBody = document.querySelector('textarea[name="contact[body]"]');
  const requestedProduct = new URLSearchParams(location.search).get('produkt');
  if (contactBody && !contactBody.value && requestedProduct && /^[a-z0-9-]{1,80}$/.test(requestedProduct)) {
    const question = window.theme?.strings?.restockQuestion || 'Dzień dobry, kiedy będzie ponownie dostępny produkt {{ product }}?';
    contactBody.value = question.replace('{{ product }}', requestedProduct.replaceAll('-', ' '));
  }

  /* ---------- ugc arrows ---------- */
  document.querySelectorAll('.ugc').forEach((sec) => {
    const track = sec.querySelector('[data-ugc-track]'); if (!track) return;
    const step = () => (track.firstElementChild?.getBoundingClientRect().width || 300) + 14;
    sec.querySelector('[data-ugc-prev]')?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    sec.querySelector('[data-ugc-next]')?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
  });

  /* ---------- spotlight slider dots ---------- */
  document.querySelectorAll('[data-spot-track]').forEach((track) => {
    const dots = track.parentElement.querySelectorAll('[data-spot-dot]');
    if (!dots.length) return;
    const slides = [...track.children];
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting && en.intersectionRatio > .6) { const i = slides.indexOf(en.target); dots.forEach((d, j) => d.classList.toggle('is-active', i === j)); } });
    }, { root: track, threshold: .6 });
    slides.forEach((sl) => io.observe(sl));
    dots.forEach((d) => d.addEventListener('click', () => slides[Number(d.dataset.spotDot)]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' })));
  });

  /* ---------- add a whole set ---------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-add-set]');
    if (!btn || btn.disabled) return;
    const ids = btn.dataset.addSet.split(',').map(Number).filter(Boolean);
    const label = btn.querySelector('[data-add-to-cart-text]');
    const original = label ? label.textContent : '';
    btn.disabled = true; if (label) label.textContent = strings.adding;
    try {
      await cart.add({ items: ids.map((id) => ({ id, quantity: 1 })) });
      if (label) label.textContent = strings.added;
      await cart.refresh(true);
    } catch (err) { toast(err.message); }
    finally { btn.disabled = false; setTimeout(() => { if (label) label.textContent = original; }, 1200); }
  });

  /* ---------- cross-sell modal (after add to cart) ---------- */
  const crossSell = {
    open(el, variantId) {
      el.classList.add('is-open'); el.setAttribute('aria-hidden', 'false'); document.body.classList.add('drawer-open');
      el.returnFocus = document.activeElement;
      el.querySelector('[data-xsell-add]')?.focus({ preventScroll: true });
      try { sessionStorage.setItem(`xsell-${variantId}`, '1'); } catch (_) {}
    },
    close(el, openCart = true) {
      el.classList.remove('is-open'); el.setAttribute('aria-hidden', 'true'); document.body.classList.remove('drawer-open');
      if (openCart) drawers.open('CartDrawer');
    },
  };
  document.addEventListener('click', async (e) => {
    const closer = e.target.closest('[data-xsell-close]');
    if (closer) { crossSell.close(closer.closest('[data-xsell]')); return; }
    const adder = e.target.closest('[data-xsell-add]');
    if (adder) {
      const el = adder.closest('[data-xsell]');
      adder.disabled = true; const original = adder.textContent; adder.textContent = strings.adding;
      try {
        await cart.add({ items: [{ id: Number(adder.dataset.xsellAdd), quantity: 1 }] });
        await cart.refresh(false);
        const page = el.closest('product-page'); if (page) delete page.dataset.xsellVariant;
        const addon = document.querySelector('[data-addon]'); if (addon) { addon.checked = false; addon.disabled = true; const box = addon.closest('.addon'); const sub = box?.querySelector('.addon__sub'); if (sub) sub.textContent = box.dataset.inCartLabel || 'Już w koszyku'; }
        crossSell.close(el);
      } catch (err) { toast(err.message); adder.disabled = false; adder.textContent = original; }
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const open = document.querySelector('[data-xsell].is-open');
    if (open) crossSell.close(open);
  });

  /* ---------- delivery choice in the cart: InPost locker or courier (before checkout) ---------- */
  const PP_KEYS = ['PickupPointCourier', 'PickupPointName', 'PickupPointId', 'PickupPointAddress', 'PickupPointPostCode', 'PickupPointCity'];
  const PP_EMPTY = () => Object.fromEntries(PP_KEYS.map((k) => [k, '']));
  const pickup = {
    loaded: false,
    failed: false, // map or cart API broke: never hold checkout after that
    modal: () => document.querySelector('[data-pp-modal]'),
    fail() {
      if (this.failed) return;
      this.failed = true;
      document.querySelectorAll('[data-pp-error]').forEach((el) => { el.hidden = true; });
    },
    load(m) {
      if (this.loaded) return;
      this.loaded = true;
      const css = document.createElement('link');
      css.rel = 'stylesheet'; css.href = 'https://geowidget.inpost.pl/inpost-geowidget.css';
      document.head.appendChild(css);
      const js = document.createElement('script');
      js.src = 'https://geowidget.inpost.pl/inpost-geowidget.js'; js.defer = true;
      js.onerror = () => this.mapDown();
      document.head.appendChild(js);
      setTimeout(() => { if (!customElements.get('inpost-geowidget')) this.mapDown(); }, 10000);
      const w = document.createElement('inpost-geowidget');
      w.setAttribute('onpoint', 'mildpointselect');
      w.setAttribute('token', m.dataset.token);
      w.setAttribute('language', m.dataset.lang || 'pl');
      w.setAttribute('config', 'parcelCollect');
      m.querySelector('[data-pp-map]').appendChild(w);
    },
    async mapDown() {
      // InPost map unavailable: record the wish for a locker and let the customer through.
      if (this.failed) return;
      this.fail();
      this.close();
      toast(strings.pickupMapFailed);
      try { await this.write({ Dostawa: 'Paczkomat', ...PP_EMPTY() }); } catch (_) {}
    },
    open() {
      const m = this.modal();
      if (!m) { this.fail(); return; }
      m.returnFocus = document.activeElement;
      this.load(m);
      if (this.failed) return;
      m.classList.add('is-open'); m.setAttribute('aria-hidden', 'false'); document.body.classList.add('drawer-open');
      m.querySelector('.ppm__close')?.focus({ preventScroll: true });
    },
    close() {
      const m = this.modal(); if (!m || !m.classList.contains('is-open')) return;
      m.classList.remove('is-open'); m.setAttribute('aria-hidden', 'true');
      if (!document.querySelector('[data-drawer].is-open')) document.body.classList.remove('drawer-open');
      if (m.returnFocus?.isConnected) m.returnFocus.focus({ preventScroll: true });
    },
    async write(attributes, msg) {
      const res = await fetch(`${routes.cart}/update.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes }),
      });
      if (!res.ok) throw new Error(strings.cartError);
      await cart.refresh(false);
      document.querySelector('.drawer.is-open [data-pp] [aria-checked="true"], [data-cart-page] [data-pp] [aria-checked="true"]')?.focus({ preventScroll: true });
      if (msg) toast(msg);
    },
    async save(point) {
      if (!point || !point.name) return;
      const a = point.address_details || {};
      const street = [a.street, [a.building_number, a.flat_number].filter(Boolean).join('/')].filter(Boolean).join(' ');
      this.close();
      try {
        await this.write({
          Dostawa: 'Paczkomat',
          PickupPointCourier: 'InPost Paczkomaty',
          PickupPointName: point.name,
          PickupPointId: point.name,
          PickupPointAddress: street || point.address?.line1 || '',
          PickupPointPostCode: a.post_code || '',
          PickupPointCity: a.city || '',
        }, strings.pickupSaved);
      } catch (err) { this.fail(); toast(err.message); }
    },
    async courier() {
      try { await this.write({ Dostawa: 'Kurier', ...PP_EMPTY() }, strings.pickupCleared); }
      catch (err) { this.fail(); toast(err.message); }
    },
  };
  document.addEventListener('mildpointselect', (e) => pickup.save(e.detail));
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-pp-open]')) { e.preventDefault(); pickup.open(); return; }
    if (e.target.closest('[data-pp-close]')) { pickup.close(); return; }
    if (e.target.closest('[data-pp-courier]')) { e.preventDefault(); pickup.courier(); }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && document.querySelector('[data-pp-modal].is-open')) { e.stopImmediatePropagation(); pickup.close(); }
  }, true);
  // Hold checkout until a delivery is chosen. Never holds once anything failed.
  document.addEventListener('submit', (e) => {
    if (pickup.failed || e.submitter?.name !== 'checkout') return;
    const box = e.target.closest('.drawer__panel, [data-cart-page]')?.querySelector('[data-pp]');
    if (!box || box.dataset.ppMode) return;
    e.preventDefault();
    const err = box.querySelector('[data-pp-error]');
    if (err) err.hidden = false;
    box.classList.add('pp--attention');
    box.scrollIntoView({ block: 'center', behavior: 'smooth' });
    box.querySelector('.pp__tile')?.focus({ preventScroll: true });
  }, true);

  /* ---------- product-page shell ---------- */
  customElements.define('product-page', class extends HTMLElement {});
  customElements.define('header-drawer-root', class extends HTMLElement {
    connectedCallback() {
      this.updatePosition = () => {
        const height = document.querySelector('.ticker')?.offsetHeight || 0;
        this.style.setProperty('--header-offset', `${Math.max(0, height - window.scrollY)}px`);
        this.classList.toggle('is-scrolled', window.scrollY > height + 100);
      };
      window.addEventListener('scroll', this.updatePosition, { passive: true });
      window.addEventListener('resize', this.updatePosition, { passive: true });
      this.updatePosition();
    }
    disconnectedCallback() {
      window.removeEventListener('scroll', this.updatePosition);
      window.removeEventListener('resize', this.updatePosition);
    }
  });
  customElements.define('cart-drawer', class extends HTMLElement {});

  /* ---------- PDP reviews: masonry wall over the Judge.me carousel ---------- */
  const wallTones = { 1: 'green', 4: 'mint', 6: 'ink', 9: 'lilac', 12: 'green', 15: 'mint', 18: 'ink', 21: 'green' };
  const dressWall = (proof) => {
    const carousel = proof.querySelector('.jm-carousel');
    if (!carousel) return;
    const items = [...carousel.querySelectorAll('.jm-review-item')];
    items.forEach((el, i) => {
      el.classList.remove('mr--green', 'mr--mint', 'mr--ink', 'mr--lilac');
      if (wallTones[i]) el.classList.add(`mr--${wallTones[i]}`);
    });
    if (items.length > 8 && !carousel.dataset.wall) {
      carousel.dataset.wall = '1';
      carousel.classList.add('is-capped');
      const btn = document.createElement('button');
      btn.type = 'button'; btn.className = 'mr-more';
      btn.textContent = proof.dataset.moreLabel ? proof.dataset.moreLabel.replace('{n}', items.length) : `Pokaż wszystkie opinie (${items.length})`;
      btn.addEventListener('click', () => { carousel.classList.remove('is-capped'); btn.remove(); });
      carousel.after(btn);
    }
  };
  document.querySelectorAll('.proof').forEach((proof) => {
    proof.style.setProperty('--mild-logo', `url('${proof.dataset.logo || ''}')`);
    let queued = false;
    const run = () => { queued = false; dressWall(proof); };
    new MutationObserver(() => { if (!queued) { queued = true; requestAnimationFrame(run); } }).observe(proof, { childList: true, subtree: true });
    run();
  });
})();

/* ---------- article table of contents (h2 -> anchors) ---------- */
(() => {
  const body = document.querySelector('[data-article-body]');
  const toc = document.querySelector('[data-toc]');
  if (!body || !toc) return;
  const heads = [...body.querySelectorAll('h2')].filter((h) => h.textContent.trim());
  if (heads.length < 2) return;
  const list = toc.querySelector('[data-toc-list]');
  const used = new Set();
  heads.forEach((h) => {
    let id = h.id || h.textContent.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/ł/g, 'l').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60) || 'sekcja';
    while (used.has(id)) id += '-2';
    used.add(id); h.id = id;
    const li = document.createElement('li'); const a = document.createElement('a');
    a.href = '#' + id; a.textContent = h.textContent.trim(); li.append(a); list.append(li);
  });
  toc.hidden = false;
})();

/* ---------- home reviews row: arrows scroll by one card ---------- */
document.querySelectorAll('.hrev').forEach((sec) => {
  const track = sec.querySelector('[data-hrev-track]'); if (!track) return;
  const prev = sec.querySelector('[data-hrev-prev]'); const next = sec.querySelector('[data-hrev-next]');
  const step = () => (track.querySelector('.hrev__card')?.getBoundingClientRect().width || 300) + 16;
  const sync = () => {
    const max = track.scrollWidth - track.clientWidth - 2;
    sec.classList.toggle('hrev--scroll', max > 0);
    if (prev) prev.disabled = track.scrollLeft <= 2;
    if (next) next.disabled = track.scrollLeft >= max;
  };
  prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
  next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
  track.addEventListener('scroll', sync, { passive: true });
  window.addEventListener('resize', sync, { passive: true });
  sync();
});

  /* ---------- lazy looping videos: load and play only while on screen ---------- */
  (() => {
    const vids = document.querySelectorAll('video[data-autoplay]');
    if (!vids.length) return;
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const io = new IntersectionObserver((entries) => entries.forEach(({ target: v, isIntersecting }) => {
      if (isIntersecting) { if (v.preload !== 'auto') v.preload = 'auto'; if (!reduce) v.play().catch(() => {}); }
      else if (!v.paused) v.pause();
    }), { rootMargin: '200px 0px' });
    vids.forEach((v) => { v.muted = true; io.observe(v); });
  })();

  /* ---------- Consentmo cookie banner in mild style (styles injected into its open shadow root) ---------- */
  (() => {
    const css = `
      .csm-cookie-box-window{background:#fff!important;color:#172521!important;border-radius:22px!important;border:1px solid #d8e3dd!important;box-shadow:0 18px 40px rgba(23,37,33,.16)!important;padding:20px 20px 18px!important;font-family:'Mona Sans',Helvetica,Arial,sans-serif!important;max-width:400px!important}
      .cc-message,.cc-message span{color:#172521!important;font:400 14px/1.5 'Mona Sans',Helvetica,Arial,sans-serif!important}
      .cc-link{color:#172521!important;text-decoration:underline!important;text-underline-offset:3px;opacity:.75}
      .csm-ccb-buttons-wrapper [role=group]{display:grid!important;grid-template-columns:1fr 1fr!important;gap:8px!important;margin-top:14px!important}
      .csm-cc-btn{border-radius:999px!important;min-height:44px!important;font:600 14px/1 'Mona Sans',Helvetica,Arial,sans-serif!important;text-transform:none!important;letter-spacing:0!important;box-shadow:none!important}
      .csm-cc-btn.cc-allow{--csm-button-background-color:#172521!important;--csm-button-text-color:#fff!important;--csm-button-outline-color:#172521!important;background:#172521!important;color:#fff!important;border:1px solid #172521!important}
      .csm-cc-btn.cc-deny{--csm-button-text-color:#172521!important;--csm-button-outline-color:#172521!important;background:#fff!important;color:#172521!important;border:1px solid #172521!important}
      .csm-cc-btn.cc-settings{grid-column:1/-1;order:3;min-height:32px!important;background:none!important;border:0!important;color:#172521!important;text-decoration:underline;text-underline-offset:3px;opacity:.7}
      .csm-cc-btn span{color:inherit!important}
      @media(max-width:600px){.csm-cookie-box-window{max-width:calc(100vw - 24px)!important;width:calc(100vw - 24px)!important;margin:0!important;box-sizing:border-box!important}.csm-cookie-box{left:12px!important;right:12px!important;bottom:12px!important;width:auto!important;padding:0!important}.csm-cookie-box-window{width:100%!important;max-width:none!important}}
    `;
    const apply = (el) => {
      const root = el.shadowRoot;
      if (!root || root.querySelector('style[data-mild]')) return !!root;
      const s = document.createElement('style'); s.dataset.mild = ''; s.textContent = css; root.appendChild(s); return true;
    };
    let tries = 0;
    const t = setInterval(() => {
      const el = document.querySelector('csm-cookie-consent');
      if ((el && apply(el)) || ++tries > 40) clearInterval(t);
    }, 250);
  })();

  /* ---------- products without reviews yet: open the Judge.me "store reviews" tab ---------- */
  (() => {
    const sec = document.querySelector('[data-proof-store]');
    if (!sec) return;
    let n = 0;
    const t = setInterval(() => {
      const tabs = [...sec.querySelectorAll('.jdgm-tab')];
      const store = tabs.find((b) => /sklep|store|shop/i.test(b.textContent));
      if (store) {
        store.click();
        tabs.filter((b) => b !== store).forEach((b) => { b.hidden = true; });
        const sel = sec.querySelector('.jdgm-tab-dropdown__select');
        if (sel) { const o = [...sel.options].find((x) => /sklep|store|shop/i.test(x.text)); if (o) { sel.value = o.value; sel.dispatchEvent(new Event('change', { bubbles: true })); } }
        clearInterval(t);
      } else if (++n > 60) clearInterval(t);
    }, 250);
  })();
